import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEUR, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, FileText, TrendingUp, Clock, Inbox } from "lucide-react";

const STATUS = {
  entwurf: { label: "Entwurf", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  gesendet: { label: "Gesendet", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  angenommen: { label: "Angenommen", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  abgelehnt: { label: "Abgelehnt", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [inqCount, setInqCount] = useState(null);

  useEffect(() => {
    api.get("/quotes").then((r) => { setQuotes(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/stats/haendler").then((r) => setStats(r.data)).catch(() => {});
      api.get("/inquiries-count").then((r) => setInqCount(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  const total = quotes.reduce((s, q) => s + (q.totals?.total || 0), 0);
  const counts = quotes.reduce((a, q) => { a[q.status] = (a[q.status] || 0) + 1; return a; }, {});
  const now = new Date();
  const monthQuotes = quotes.filter((q) => { const d = new Date(q.createdAt); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
  const monthTotal = monthQuotes.reduce((s, q) => s + (q.totals?.total || 0), 0);
  const monthLabel = now.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevQuotes = quotes.filter((q) => { const d = new Date(q.createdAt); return d.getMonth() === prevDate.getMonth() && d.getFullYear() === prevDate.getFullYear(); });
  const prevTotal = prevQuotes.reduce((s, q) => s + (q.totals?.total || 0), 0);
  const prevLabel = prevDate.toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 md:mb-10 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Übersicht</div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-zinc-900">
            Guten Tag, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isAdmin ? "Alle Angebote im System" : "Ihre Angebote"} — Stand {fmtDate(new Date())}
          </p>
        </div>
        <Link
          to="/quote/new"
          data-testid="dashboard-new-quote-button"
          className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center justify-center gap-2 transition-colors"
        >
          <Plus size={15} /> Neues Angebot
        </Link>
      </div>

      {isAdmin && inqCount && (
        <Link to="/inquiries" data-testid="dashboard-inquiries-link"
              className={`flex items-center justify-between gap-4 border px-4 py-3 mb-4 transition-colors ${inqCount.neu > 0 ? "bg-amber-50 border-amber-300 hover:bg-amber-100" : "bg-white border-zinc-200 hover:bg-zinc-50"}`}>
          <div className="flex items-center gap-3">
            <span className={`w-9 h-9 flex items-center justify-center ${inqCount.neu > 0 ? "bg-amber-500 text-white" : "bg-zinc-100 text-zinc-600"}`}><Inbox size={16}/></span>
            <div>
              <div className="text-sm font-semibold text-zinc-900"><span data-testid="dashboard-inquiries-new">{inqCount.neu}</span> neue Anfrage{inqCount.neu === 1 ? "" : "n"} aus dem Online-Zaunplaner</div>
              <div className="text-xs text-zinc-500">{inqCount.total} Anfrage{inqCount.total === 1 ? "" : "n"} gesamt · zur Liste →</div>
            </div>
          </div>
        </Link>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-200 mb-4">
        <KPI label="Angebote" value={quotes.length} icon={FileText} />
        <KPI label="Entwurf" value={counts.entwurf || 0} icon={Clock} />
        <KPI label="Angenommen" value={counts.angenommen || 0} icon={TrendingUp} />
        <KPI label="Volumen (netto)" value={fmtEUR(total)} icon={TrendingUp} mono />
      </div>

      {/* Aktueller + Vormonat (nur netto – relevant für Firmen & Privat) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 md:mb-10">
        <div className="grid grid-cols-2 gap-0 border border-zinc-900 bg-zinc-900 text-white" data-testid="dashboard-month-stats">
          <div className="px-5 py-4 border-r border-white/15">
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Angebote {monthLabel}</div>
            <div className="text-2xl font-semibold tabular-nums" data-testid="dashboard-month-count">{monthQuotes.length}</div>
          </div>
          <div className="px-5 py-4">
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">Volumen {monthLabel} (netto)</div>
            <div className="text-2xl font-semibold font-mono tabular-nums" data-testid="dashboard-month-netto">{fmtEUR(monthTotal)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-0 border border-zinc-300 bg-white text-zinc-900" data-testid="dashboard-prev-month-stats">
          <div className="px-5 py-4 border-r border-zinc-200">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Angebote {prevLabel}</div>
            <div className="text-2xl font-semibold tabular-nums" data-testid="dashboard-prev-month-count">{prevQuotes.length}</div>
          </div>
          <div className="px-5 py-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Volumen {prevLabel} (netto)</div>
            <div className="text-2xl font-semibold font-mono tabular-nums" data-testid="dashboard-prev-month-netto">{fmtEUR(prevTotal)}</div>
          </div>
        </div>
      </div>

      {/* Händler-Statistik (nur Admin, intern) */}
      {isAdmin && stats.length > 0 && (
        <div className="border border-zinc-200 mb-8 md:mb-10" data-testid="haendler-stats-section">
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Händler-Statistik</div>
            <div className="text-sm font-semibold text-zinc-900">Angebote pro Benutzer <span className="text-zinc-400 font-normal">(nur intern sichtbar)</span></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-5 py-2.5 font-medium">Benutzer</th>
                  <th className="text-left px-5 py-2.5 font-medium hidden sm:table-cell">Rolle</th>
                  <th className="text-right px-5 py-2.5 font-medium">Angebote</th>
                  <th className="text-right px-5 py-2.5 font-medium">Volumen</th>
                  <th className="text-right px-5 py-2.5 font-medium hidden md:table-cell">Angenommen</th>
                  <th className="text-right px-5 py-2.5 font-medium hidden md:table-cell">AB/Proforma</th>
                  <th className="text-right px-5 py-2.5 font-medium hidden lg:table-cell">Letztes Angebot</th>
                </tr>
              </thead>
              <tbody data-testid="haendler-stats-body">
                {stats.map((s) => (
                  <tr key={s.userId} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-5 py-3">
                      <div className="text-zinc-900">{s.name}{!s.active && <span className="ml-2 text-[10px] text-red-500 uppercase">inaktiv</span>}</div>
                      <div className="text-xs text-zinc-500">{s.email}</div>
                    </td>
                    <td className="px-5 py-3 hidden sm:table-cell">
                      <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border ${s.role === "admin" ? "bg-zinc-900 text-white border-zinc-900" : "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                        {s.role === "admin" ? "Admin" : "Händler"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono">{s.quoteCount}</td>
                    <td className="px-5 py-3 text-right font-mono text-zinc-900">{fmtEUR(s.totalValue)}</td>
                    <td className="px-5 py-3 text-right font-mono hidden md:table-cell">
                      {s.acceptedCount} <span className="text-zinc-400 text-xs">({fmtEUR(s.acceptedValue)})</span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono hidden md:table-cell">{s.abCount}</td>
                    <td className="px-5 py-3 text-right font-mono text-xs text-zinc-600 hidden lg:table-cell">
                      {s.lastQuoteAt ? fmtDate(s.lastQuoteAt) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent quotes */}
      <div className="border border-zinc-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-zinc-50">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Letzte Angebote</div>
            <div className="text-sm font-semibold text-zinc-900">Aktuelle Aktivität</div>
          </div>
          <Link to="/quotes" className="text-xs text-zinc-600 hover:text-zinc-900 underline underline-offset-2">
            Alle anzeigen →
          </Link>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">Wird geladen...</div>
        ) : quotes.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <div className="text-sm text-zinc-500 mb-2">Noch keine Angebote</div>
            <Link to="/quote/new" className="text-sm font-medium text-zinc-900 underline underline-offset-2">
              Erstes Angebot erstellen →
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">Nummer</th>
                <th className="text-left px-5 py-2.5 font-medium">Kunde</th>
                <th className="text-left px-5 py-2.5 font-medium hidden md:table-cell">Projekt</th>
                <th className="text-left px-5 py-2.5 font-medium hidden sm:table-cell">Datum</th>
                <th className="text-left px-5 py-2.5 font-medium">Status</th>
                <th className="text-right px-5 py-2.5 font-medium">Gesamt</th>
              </tr>
            </thead>
            <tbody data-testid="quotes-table-body">
              {quotes.slice(0, 8).map((q) => {
                const st = STATUS[q.status] || STATUS.entwurf;
                return (
                  <tr key={q.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-5 py-3 font-mono text-xs">
                      <Link to={`/quote/${q.id}`} className="text-zinc-900 hover:underline" data-testid={`quote-link-${q.quoteNumber}`}>
                        {q.quoteNumber}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-zinc-900">
                      {q.customer?.company || q.customer?.name || "—"}
                    </td>
                    <td className="px-5 py-3 text-zinc-600 hidden md:table-cell">{q.project?.name || "—"}</td>
                    <td className="px-5 py-3 text-zinc-600 font-mono text-xs hidden sm:table-cell">{fmtDate(q.createdAt)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border ${st.cls}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-zinc-900">{fmtEUR(q.totals?.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, mono }) {
  return (
    <div className="px-5 py-4 border-r border-zinc-200 last:border-r-0">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
          <div className={`text-2xl font-semibold text-zinc-900 ${mono ? "font-mono tabular-nums" : ""}`}>
            {value}
          </div>
        </div>
        <Icon size={14} className="text-zinc-400 mt-1" />
      </div>
    </div>
  );
}
